/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';

const useStyles = makeStyles(() => ({
  actionsTile : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : 'white',
    padding         : 10,
    alignItems      : 'center'
  },
  tableContainer : {
    height : '360px'
  },
  listbox : {
    padding : '0px !important',
    margin  : '0px !important'
  },
  popper : {
    zIndex : dlsZIndex('deviceTypeChooserPopper')
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  inputRoot : {
    '&::before' : {
      borderBottom : `1px solid ${dlsColor('grey4')} !important`
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 * React Hook to provide styles for Job Task chooser components.
 * 
 *  ```typescript
 *   import {useJobTaskChooserStyles} from './useJobTaskChooserStyles';
 * 
 *   const {dropdownProps} = useJobTaskChooserStyles();
 *  ```
 */
export const useJobTaskChooserStyles = () => {
  const classes       = useStyles();
  const dropdownProps = useMemo(() => ({
    popper         : classes.popper,
    paper          : classes.paper,
    popupIndicator : classes.popupIndicator,
    clearIndicator : classes.clearIndicator,
    endAdornment   : classes.endAdornment,
    input          : classes.input,
    inputRoot      : classes.inputRoot,
    listbox        : classes.listbox,
    option         : classes.option
  }), []);

  return {
    dropdownProps
  };
};

export default useJobTaskChooserStyles;
